/* AP review: validate external engraving fields before Dawn's AJAX submit. */
(function () {
  if (window.apEngravingFormGuard) return;
  window.apEngravingFormGuard = true;
  document.addEventListener('input', function (event) {
    if (event.target.matches('ap-engraving input')) event.target.setCustomValidity('');
  }, true);
  document.addEventListener('submit', function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const fields = document.querySelectorAll('ap-engraving [data-ap-line]');
    for (const line of fields) {
      const input = line.querySelector('input');
      if (!input || input.disabled || input.form !== form) continue;
      input.setCustomValidity('');
      if (line.dataset.lineRequired === 'true' && !input.value.trim()) {
        input.setCustomValidity('Please add the engraving text.');
      }
      if (!input.checkValidity()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        input.reportValidity();
        input.focus();
        return;
      }
    }
  }, true);
})();

/* Ascending Pets: engraving add-on for main-product (Dawn 16).
 *
 * <ap-engraving> modes (set by snippets/ap-product-block.liquid):
 * - "toggle": the product has an option (default "Engraving") with values "No engraving" / "Add engraving".
 *   A checkbox ("Add engraving (+$15)") is the one visible control. Checking it selects the "Add engraving"
 *   value in Dawn's own (visually hidden) variant picker and dispatches that input's `change` event, so Dawn 16's
 *   VariantSelects -> PUB_SUB_EVENTS.optionValueSelectionChange -> product-info flow fetches the variant and
 *   updates the displayed price, the form's hidden variant id and the URL (?variant=...). Unchecking selects
 *   "No engraving". The text line(s) are shown, enabled and required only while checked. After Dawn re-renders
 *   (PUB_SUB_EVENTS.variantChange) the checkbox is re-synced from the selected variant JSON (authoritative).
 * - "always": no such option on the product; the line(s) are always shown, `required` applies as set.
 *
 * Text lines are inputs named properties[Engraving] (1 line) or properties[Engraving line 1] /
 * properties[Engraving line 2] (2 lines; line 2 is always optional), tied to the product form with the
 * form="product-form-{section.id}" attribute. Dawn's product-form.js posts `new FormData(form)`, which includes
 * form-associated inputs outside the <form>. Disabled inputs are never posted.
 *
 * Validation: Dawn's form is `novalidate` and submits by fetch, so a capture-phase listener on document runs first
 * and blocks the add when (a) a required visible line is empty, (b) a line is over the limit, or (c) in toggle mode
 * the form's variant id doesn't match the checkbox yet (variant fetch still in flight). Empty optional lines are
 * disabled for the synchronous part of the submit so no blank property is added.
 */
(function () {
  if (customElements.get('ap-engraving')) return;

  const lower = (v) => String(v == null ? '' : v).trim().toLowerCase();

  class APEngraving extends HTMLElement {
    connectedCallback() {
      this.lines = Array.from(this.querySelectorAll('[data-ap-line]')).map((wrap) => ({
        wrap,
        input: wrap.querySelector('input'),
        counter: wrap.querySelector('[data-ap-count]'),
        required: wrap.dataset.lineRequired === 'true',
      }));
      this.lines = this.lines.filter((line) => line.input);
      if (!this.lines.length) return;
      this.input = this.lines[0].input; // used to find the associated form
      this.fields = this.querySelector('[data-ap-fields]');
      this.toggle = this.querySelector('[data-ap-toggle]');
      this.error = this.querySelector('[data-ap-error]');
      this.wait = this.querySelector('[data-ap-wait]');
      this.mode = this.dataset.mode === 'toggle' && this.toggle ? 'toggle' : 'always';
      this.isRequired = this.dataset.required === 'true';
      this.max = parseInt(this.dataset.max, 10) || 0;
      this.optionName = lower(this.dataset.optionName);
      this.optionIndex = parseInt(this.dataset.optionIndex, 10);
      this.valueOn = lower(this.dataset.valueOn);
      this.valueOff = lower(this.dataset.valueOff);
      this.onVariants = String(this.dataset.onVariants || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      this.productInfo = this.closest('product-info');

      this.onInput = () => {
        this.updateCounts();
        this.clearError();
      };
      this.lines.forEach((line) => line.input.addEventListener('input', this.onInput));
      this.updateCounts();

      this.unsubscribers = [];
      if (this.mode === 'toggle') {
        if (this.productInfo) this.productInfo.setAttribute('data-ap-engraving-ready', '');
        this.hideNative();
        this.onToggle = () => {
          this.setShown(this.toggle.checked);
          this.selectNative(this.toggle.checked);
        };
        this.toggle.addEventListener('change', this.onToggle);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
              const data = (payload && payload.data) || {};
              if (this.productInfo && data.sectionId && this.productInfo.sectionId && data.sectionId !== this.productInfo.sectionId) return;
              this.hideNative(); // Dawn replaced <variant-selects>
              if (data.variant && Array.isArray(data.variant.options)) this.syncFromOptions(data.variant.options);
              else this.syncFromSelectedVariant();
            })
          );
        }
        this.syncFromSelectedVariant();
      } else {
        this.setShown(true);
      }
    }

    disconnectedCallback() {
      (this.lines || []).forEach((line) => line.input.removeEventListener('input', this.onInput));
      if (this.toggle && this.onToggle) this.toggle.removeEventListener('change', this.onToggle);
      (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
      this.unsubscribers = [];
    }

    get variantSelects() {
      return (this.productInfo || document).querySelector('variant-selects');
    }

    /* Dawn's inputs / <option>s for our option (they carry data-option-name). */
    nativeOptionNodes() {
      const selects = this.variantSelects;
      if (!selects) return [];
      return Array.from(selects.querySelectorAll('[data-option-name]')).filter(
        (node) => lower(node.dataset.optionName) === this.optionName
      );
    }

    /* The checkbox is the one control: take Dawn's (visually hidden) Engraving group out of the reading and tab
       order. `inert` + aria-hidden block user focus/clicks only; we still set it and dispatch `change` from code. */
    hideNative() {
      const groups = new Set();
      this.nativeOptionNodes().forEach((node) => {
        const group = node.closest('fieldset, .product-form__input--dropdown');
        if (group) groups.add(group);
        const focusable = node.tagName === 'OPTION' ? node.closest('select') : node;
        if (focusable) focusable.setAttribute('tabindex', '-1');
      });
      groups.forEach((group) => {
        group.setAttribute('aria-hidden', 'true');
        group.inert = true;
        group.setAttribute('inert', '');
      });
    }

    /* Select "Add engraving" / "No engraving" in Dawn's picker and let Dawn's change flow do the rest. */
    selectNative(on) {
      const want = on ? this.valueOn : this.valueOff;
      const node = this.nativeOptionNodes().find((n) => lower(n.value) === want);
      if (!node) return false;
      if (node.tagName === 'OPTION') {
        const select = node.closest('select');
        if (!select || select.value === node.value) return true;
        select.value = node.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        if (node.checked) return true;
        node.checked = true;
        node.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return true;
    }

    syncFromOptions(options) {
      if (!Array.isArray(options) || !(this.optionIndex >= 0) || options[this.optionIndex] == null) return false;
      const on = lower(options[this.optionIndex]) === this.valueOn;
      this.toggle.checked = on;
      this.setShown(on);
      return true;
    }

    /* Authoritative: the JSON Dawn renders inside <variant-selects> for the selected variant. */
    syncFromSelectedVariant() {
      const selects = this.variantSelects;
      const script = selects && selects.querySelector('[data-selected-variant]');
      if (script) {
        try {
          const variant = JSON.parse(script.textContent);
          if (variant && this.syncFromOptions(variant.options)) return;
        } catch (e) {
          /* fall through */
        }
      }
      this.setShown(this.toggle.checked);
    }

    setShown(shown) {
      if (this.fields) this.fields.hidden = !shown;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', shown ? 'true' : 'false');
      this.lines.forEach((line) => {
        line.input.disabled = !shown;
        const req = shown && line.required;
        line.input.required = req;
        if (req) line.input.setAttribute('aria-required', 'true');
        else line.input.removeAttribute('aria-required');
      });
      if (!shown) this.clearError();
      if (this.wait) this.wait.hidden = true;
    }

    get shown() {
      return this.mode === 'always' || (this.toggle && this.toggle.checked);
    }

    updateCounts() {
      this.lines.forEach((line) => {
        if (line.counter) line.counter.textContent = `${line.input.value.length} / ${this.max}`;
      });
    }

    showError(line) {
      if (this.error) this.error.hidden = false;
      line.input.setAttribute('aria-invalid', 'true');
      if (this.error && this.error.id) line.input.setAttribute('aria-errormessage', this.error.id);
    }

    clearError() {
      if (this.error) this.error.hidden = true;
      if (this.wait) this.wait.hidden = true;
      (this.lines || []).forEach((line) => line.input.removeAttribute('aria-invalid'));
    }

    /* In toggle mode the variant in the form must match the checkbox before anything is added. */
    variantMatches(form) {
      if (this.mode !== 'toggle' || !this.onVariants.length) return true;
      const idInput = form && form.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) return true;
      const variantIsOn = this.onVariants.indexOf(String(idInput.value)) !== -1;
      return variantIsOn === this.toggle.checked;
    }

    /* Returns the first invalid input (or the toggle), or null when the form may proceed. */
    validate(form) {
      this.clearError();
      if (!this.variantMatches(form)) {
        if (this.wait) this.wait.hidden = false;
        this.selectNative(this.toggle.checked);
        return this.toggle;
      }
      if (!this.shown) return null;
      let firstInvalid = null;
      this.lines.forEach((line) => {
        if (line.input.disabled) return;
        const value = line.input.value.trim();
        if (line.input.value !== value) line.input.value = value;
        const bad = (line.required && value === '') || (this.max && value.length > this.max);
        if (bad) {
          this.showError(line);
          if (!firstInvalid) firstInvalid = line.input;
        }
      });
      this.updateCounts();
      return firstInvalid;
    }
  }

  customElements.define('ap-engraving', APEngraving);

  function fieldsForForm(form) {
    if (!form || !form.id) return [];
    return Array.from(document.querySelectorAll('ap-engraving')).filter(
      (el) => el.input && el.input.getAttribute('form') === form.id
    );
  }

  /* Validate; on failure focus the first invalid field. Empty optional lines are disabled for this tick. */
  function guard(form, event) {
    const fields = fieldsForForm(form);
    if (!fields.length) return;
    const invalid = fields.map((field) => field.validate(form)).filter(Boolean);
    if (invalid.length) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalid[0].focus();
      return;
    }
    fields.forEach((field) => {
      field.lines.forEach((line) => {
        if (!line.input.disabled && line.input.value === '') {
          line.input.disabled = true;
          setTimeout(() => {
            if (field.shown) line.input.disabled = false;
          }, 0);
        }
      });
    });
  }

  /* Add to cart (Dawn product-form submit) */
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) guard(event.target, event);
    },
    true
  );

  /* Dynamic checkout ("Buy it now" etc.; off in all AP templates). Works for buttons in the page / shadow DOM.
     Wallet buttons that render inside cross-origin iframes cannot be intercepted; see README. */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      const button = target.closest('.shopify-payment-button, shopify-accelerated-checkout, shopify-buy-it-now-button');
      if (!button) return;
      const form = button.closest('form');
      if (form) guard(form, event);
    },
    true
  );
})();

/* Ascending Pets: AP – Add-on checklist (block ap_addon_checklist, build-spec §3c).
 *
 * - Rows are real checkboxes inside <label>s; nothing is pre-ticked. Engravable rows (data-variant-eng present) reveal
 *   "Add engraving (+$15)" only while the row is ticked; that reveals a 1-line text field (maxlength, live "n / 12").
 *   Unticking a row clears and hides its engraving. If a product has no Engraving variants yet, Liquid renders no
 *   engraving controls at all, so the row simply adds its first available variant.
 * - Running total = package variant price (Dawn's selected variant; follows PUB_SUB_EVENTS.variantChange) + each ticked
 *   row (No engraving variant price, or the Add engraving variant price when engraving is ticked). It is written into the
 *   main CHOOSE button ("CHOOSE SIGNATURE · $245") and the sticky bar, and announced in a polite live region after each
 *   change (never on load). Whole-dollar totals drop ".00" (theme money format).
 * - Show more: rows past the desktop/mobile count are display:none until the button is pressed; then all rows show,
 *   the button hides (aria-expanded="true") and focus moves to the first newly shown checkbox.
 * - One click, one request: a capture-phase submit listener (registered after the engraving guard, which runs first)
 *   takes over Dawn's product-form submit when at least one row is ticked, and sends ONE POST /cart/add.js with an
 *   `items` array: the package line (id, quantity and every non-empty properties[...] field Dawn's form would post),
 *   then each ticked add-on (properties.Engraving when engraved). Then it follows Dawn: cartUpdate event, cart drawer /
 *   notification renderContents, or redirect to /cart when the theme has no drawer. With nothing ticked, Dawn's own
 *   submit runs unchanged. Errors (e.g. 422) show in a role="alert" line; a cart snapshot taken before the add is
 *   compared afterwards so a partial add names what did and didn't go in.
 */
(function () {
  if (customElements.get('ap-addon-checklist')) return;
  const MQ = '(max-width: 749px)';
  const ENGRAVING_PROPERTY = 'Engraving';

  function formatMoney(cents, format) {
    const value = Math.round(Number(cents) || 0);
    const whole = value % 100 === 0;
    const num = (decimals, thousands, decimal) => {
      const parts = (value / 100).toFixed(decimals).split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? int + decimal + parts[1] : int;
    };
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return num(0, ',', '.');
        case 'amount_with_comma_separator':
          return num(whole ? 0 : 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return num(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return num(whole ? 0 : 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return num(0, ' ', ',');
        case 'amount_with_space_separator':
          return num(whole ? 0 : 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return num(whole ? 0 : 2, ' ', '.');
        default:
          return num(whole ? 0 : 2, ',', '.');
      }
    });
  }

  class APAddonChecklist extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.productFormId;
      this.base = parseInt(this.dataset.basePrice, 10) || 0;
      this.baseLabel = (this.dataset.chooseLabel || '').trim();
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.liveText = this.dataset.liveText || 'Total {total} with your keepsakes.';
      this.engMax = parseInt(this.dataset.engMax, 10) || 12;
      this.live = this.querySelector('[data-ap-addon-live]');
      this.errorEl = this.querySelector('[data-ap-addon-error]');
      this.more = this.querySelector('[data-ap-addon-more]');
      this.list = this.querySelector('[data-ap-addon-list]');
      this.rows = Array.from(this.querySelectorAll('[data-ap-addon-row]')).map((li) => {
        const q = (s) => li.querySelector(s);
        return {
          li,
          title: li.dataset.title || '',
          check: q('[data-ap-addon-check]'),
          eng: q('[data-ap-addon-eng]'),
          engCheck: q('[data-ap-addon-eng-check]'),
          engField: q('[data-ap-addon-eng-field]'),
          engText: q('[data-ap-addon-eng-text]'),
          engCount: q('[data-ap-addon-eng-count]'),
          engError: q('[data-ap-addon-eng-error]'),
          variantNo: li.dataset.variantNo,
          variantEng: li.dataset.variantEng || '',
          priceNo: parseInt(li.dataset.priceNo, 10) || 0,
          priceEng: parseInt(li.dataset.priceEng, 10) || 0,
        };
      });
      this.rows.forEach((row) => {
        row.engravable = !!(row.variantEng && row.engCheck && row.engText);
        row.check.checked = false; // never pre-ticked (also after back/forward cache restores)
        if (row.engCheck) row.engCheck.checked = false;
        if (row.engText) row.engText.value = '';
      });

      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => {
        const row = this.rows.find((r) => r.engText === event.target);
        if (row) {
          this.updateCount(row);
          this.clearEngError(row);
        }
      });
      if (this.more) this.more.addEventListener('click', () => this.expand());

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
          const data = (payload && payload.data) || {};
          if (data.sectionId && data.sectionId !== this.sectionId) return;
          if (data.variant && data.variant.price != null) this.base = Number(data.variant.price) || 0;
          this.update(false);
        });
      }

      this.main = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (this.main) {
        this.mo = new MutationObserver(() => this.applyLabel());
        this.mo.observe(this.main, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
      }
      this.update(false);
    }

    disconnectedCallback() {
      if (this.mo) this.mo.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      this.ready = false;
    }

    get isMobile() {
      return window.matchMedia ? window.matchMedia(MQ).matches : false;
    }

    onChange(event) {
      const target = event.target;
      const row = this.rows.find((r) => r.check === target || r.engCheck === target);
      if (!row) return;
      this.clearCartError();
      if (target === row.check && !row.check.checked && row.engravable) {
        row.engCheck.checked = false;
        row.engText.value = '';
        this.updateCount(row);
        this.clearEngError(row);
      }
      if (target === row.engCheck && !row.engCheck.checked) {
        row.engText.value = '';
        this.updateCount(row);
        this.clearEngError(row);
      }
      this.update(true);
      if (target === row.engCheck && row.engCheck.checked) row.engText.focus();
    }

    syncRow(row) {
      const on = row.check.checked;
      row.li.classList.toggle('is-on', on);
      if (!row.engravable) return;
      row.eng.hidden = !on;
      row.check.setAttribute('aria-expanded', on ? 'true' : 'false');
      const engOn = on && row.engCheck.checked;
      row.engField.hidden = !engOn;
      row.engCheck.setAttribute('aria-expanded', engOn ? 'true' : 'false');
      row.engText.disabled = !engOn;
      if (engOn) {
        row.engText.required = true;
        row.engText.setAttribute('aria-required', 'true');
      } else {
        row.engText.required = false;
        row.engText.removeAttribute('aria-required');
      }
    }

    engOn(row) {
      return !!(row.engravable && row.check.checked && row.engCheck.checked);
    }

    ticked() {
      return this.rows.filter((row) => row.check.checked);
    }

    total() {
      return this.ticked().reduce((sum, row) => sum + (this.engOn(row) ? row.priceEng : row.priceNo), this.base);
    }

    money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    labelText() {
      const total = this.money(this.total());
      return this.baseLabel ? `${this.baseLabel} · ${total}` : total;
    }

    update(announce) {
      this.rows.forEach((row) => this.syncRow(row));
      this.applyLabel();
      document.querySelectorAll('ap-sticky-choose').forEach((bar) => {
        if (bar.dataset.sectionId === this.sectionId && typeof bar.sync === 'function') bar.sync();
      });
      if (announce && this.live) this.live.textContent = this.liveText.replace('{total}', this.money(this.total()));
    }

    /* The main CHOOSE button carries the running total. Dawn's own states win: while it's disabled (sold out,
       unavailable, variant fetch) its text is left alone; Dawn resets to "Add to cart" afterwards and we re-apply. */
    applyLabel() {
      if (!this.main || this.busy || this.main.disabled) return;
      const span = this.main.querySelector('span') || this.main;
      const want = this.labelText();
      if (span.textContent.trim() !== want) span.textContent = want;
    }

    updateCount(row) {
      if (row.engCount) row.engCount.textContent = `${row.engText.value.length} / ${this.engMax}`;
    }

    clearEngError(row) {
      if (!row.engText) return;
      row.engText.removeAttribute('aria-invalid');
      row.engText.removeAttribute('aria-errormessage');
      if (row.engError) row.engError.hidden = true;
    }

    expand() {
      if (!this.more || !this.list) return;
      const shown = parseInt(this.isMobile ? this.dataset.visibleMobile : this.dataset.visibleDesktop, 10) || 0;
      this.list.classList.add('is-expanded');
      this.more.setAttribute('aria-expanded', 'true');
      this.more.hidden = true;
      const next = this.rows[shown];
      if (next) next.check.focus();
    }

    /* Returns the first engraving field that blocks the add (and shows its message), or null. */
    validate() {
      let first = null;
      this.ticked().forEach((row) => {
        if (!this.engOn(row)) return;
        const value = row.engText.value.trim();
        if (row.engText.value !== value) row.engText.value = value;
        this.updateCount(row);
        if (value.length < 1 || value.length > this.engMax) {
          row.engText.setAttribute('aria-invalid', 'true');
          if (row.engError) {
            row.engError.hidden = false;
            row.engText.setAttribute('aria-errormessage', row.engError.id);
          }
          if (!first) first = row.engText;
        } else {
          this.clearEngError(row);
        }
      });
      return first;
    }

    buildItems(form) {
      const fd = new FormData(form);
      const id = fd.get('id');
      if (!id) return null;
      const pkg = { id: Number(id), quantity: parseInt(fd.get('quantity'), 10) || 1 };
      const properties = {};
      for (const [key, value] of fd.entries()) {
        const m = /^properties\[(.+)\]$/.exec(key);
        if (m && typeof value === 'string' && value.trim() !== '') properties[m[1]] = value;
      }
      if (Object.keys(properties).length) pkg.properties = properties;
      const plan = fd.get('selling_plan');
      if (plan) pkg.selling_plan = Number(plan);
      const items = [pkg];
      this.ticked().forEach((row) => {
        const engraved = this.engOn(row);
        const item = { id: Number(engraved ? row.variantEng : row.variantNo), quantity: 1 };
        if (engraved) item.properties = { [ENGRAVING_PROPERTY]: row.engText.value.trim() };
        items.push(item);
      });
      return items;
    }

    showCartError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearCartError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    buttons() {
      const list = [];
      if (this.main) list.push(this.main);
      document.querySelectorAll('ap-sticky-choose').forEach((bar) => {
        if (bar.dataset.sectionId !== this.sectionId) return;
        const b = bar.querySelector('[data-ap-sticky-button]');
        if (b) list.push(b);
      });
      return list;
    }

    setBusy(busy) {
      this.busy = busy;
      this.buttons().forEach((b) => {
        if (busy) {
          b.dataset.apWasDisabled = b.disabled ? 'true' : 'false';
          b.disabled = true;
          b.setAttribute('aria-busy', 'true');
        } else {
          b.removeAttribute('aria-busy');
          if (b.dataset.apWasDisabled !== 'true') b.disabled = false;
          delete b.dataset.apWasDisabled;
        }
      });
      if (!busy) this.update(false);
    }

    async cartSnapshot() {
      try {
        const res = await fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        const counts = {};
        (cart.items || []).forEach((item) => {
          counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity;
        });
        return counts;
      } catch (e) {
        return null;
      }
    }

    handleSubmit(event, form) {
      if (!this.ticked().length) return; // nothing ticked: Dawn's own single add runs unchanged
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;
      this.clearCartError();
      const invalid = this.validate();
      if (invalid) {
        invalid.focus();
        return;
      }
      const submit = form.querySelector('[type="submit"]');
      if (submit && (submit.disabled || submit.getAttribute('aria-disabled') === 'true')) return;
      const items = this.buildItems(form);
      if (!items) return;
      const names = [this.dataset.productTitle || ''].concat(this.ticked().map((row) => row.title));
      this.send(items, names);
    }

    async send(items, names) {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id).join(',');
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
      const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`.replace(/\.js\.js$/, '.js');
      this.setBusy(true);
      const before = await this.cartSnapshot();
      let response;
      try {
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        response = await res.json();
        if (!res.ok && !response.status) response.status = res.status;
      } catch (e) {
        this.setBusy(false);
        this.showCartError(this.dataset.msgNetwork || "We couldn't reach the cart. Please try again.");
        return;
      }

      if (response.status) {
        await this.reportFailure(response, items, names, before);
        this.setBusy(false);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: items[0].id, errors: response.errors || response.description, message: response.message });
        }
        return;
      }

      this.setBusy(false);
      // Success: clear every ticked row (and engraving) so the same keepsakes can't be added twice by accident.
      this.reset();
      if (!cart) {
        window.location = (window.routes && window.routes.cart_url) || '/cart';
        return;
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: items[0].id, cartData: response });
      }
      const first = (response.items && response.items[0]) || {};
      const state = Object.assign({}, response, { id: first.id, key: first.key });
      const render = () => {
        cart.renderContents(state);
        if (cart.classList && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      };
      const modal = this.closest('quick-add-modal');
      if (modal) {
        document.body.addEventListener('modalClosed', () => setTimeout(render), { once: true });
        modal.hide(true);
      } else {
        render();
      }
    }

    /* After a successful add: untick every row, clear and hide the engraving checkbox and text, clear errors, and put
       the main CHOOSE button and the sticky bar back to the package price. Nothing is announced (the cart drawer
       opening is the confirmation); the live region is emptied so a stale total is never re-read. */
    reset() {
      this.rows.forEach((row) => {
        row.check.checked = false;
        if (row.engCheck) row.engCheck.checked = false;
        if (row.engText) {
          row.engText.value = '';
          this.updateCount(row);
          this.clearEngError(row);
        }
      });
      this.clearCartError();
      if (this.live) this.live.textContent = '';
      this.update(false);
    }

    /* All-or-nothing check: compare the cart with the snapshot from before the add. */
    async reportFailure(response, items, names, before) {
      const reason = String(response.description || response.message || '').trim();
      const after = before ? await this.cartSnapshot() : null;
      let message = reason;
      if (before && after) {
        const want = {};
        items.forEach((item) => {
          want[item.id] = (want[item.id] || 0) + item.quantity;
        });
        const gained = {};
        Object.keys(want).forEach((id) => {
          gained[id] = Math.max(0, (after[id] || 0) - (before[id] || 0));
        });
        const added = [];
        const missed = [];
        items.forEach((item, i) => {
          if (gained[item.id] >= item.quantity) {
            gained[item.id] -= item.quantity;
            added.push(names[i]);
          } else {
            missed.push(names[i]);
          }
        });
        if (added.length) {
          message = `${reason} ${this.dataset.msgAdded || 'Added to your cart:'} ${added.join(', ')}. ${this.dataset.msgNotAdded || 'Not added:'} ${missed.join(', ')}.`;
          this.refreshBubble();
        } else {
          message = `${reason} ${this.dataset.msgNoneAdded || 'Nothing was added to your cart.'}`;
        }
      }
      this.showCartError(message.trim());
    }

    async refreshBubble() {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      try {
        const res = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
        const data = await res.json();
        const html = new DOMParser().parseFromString(data['cart-icon-bubble'] || '', 'text/html');
        const inner = html.querySelector('.shopify-section');
        if (inner) bubble.innerHTML = inner.innerHTML;
      } catch (e) {
        /* the bubble updates on the next page load */
      }
    }
  }

  customElements.define('ap-addon-checklist', APAddonChecklist);
  window.APAddonChecklist = { formatMoney };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.id) return;
      const checklist = Array.from(document.querySelectorAll('ap-addon-checklist')).find(
        (el) => el.formId === form.id && typeof el.handleSubmit === 'function'
      );
      if (checklist) checklist.handleSubmit(event, form);
    },
    true
  );
})();

/* Ascending Pets: sticky CHOOSE bar for package pages (block ap_sticky_choose, phones < 750px).
 *
 * - Shows only after the main buy button (#ProductSubmitButton-{section}) has scrolled out of view ABOVE the
 *   viewport, and hides as soon as it is back (IntersectionObserver; no slide or fade). On first load, while the
 *   main button is still below the fold, the bar stays hidden so it never covers "What's included".
 * - Clicking the bar's button clicks the main submit button, so the same product form, the engraving guard and
 *   Dawn's cart flow run. Label, disabled state ("Sold out") and price mirror the main button / price block,
 *   also after Dawn's variant re-render (MutationObserver).
 * - Main button label: main-product.liquid already renders the CHOOSE label server-side; Dawn's
 *   toggleSubmitButton() resets it to window.variantStrings.addToCart after a variant change, so it's re-applied.
 * - Hidden with the `hidden` attribute (out of the reading/tab order). If focus is inside the bar when it hides,
 *   focus moves to the main button. Bottom padding is added to <body> while shown so nothing is covered.
 */
(function () {
  if (customElements.get('ap-sticky-choose')) return;
  const MQ = '(max-width: 749px)';

  class APStickyChoose extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal, .quick-add-modal')) {
        this.hidden = true;
        return;
      }
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('[data-ap-sticky-button]');
      this.priceEl = this.querySelector('[data-ap-sticky-price]');
      this.label = this.button ? (this.button.dataset.label || this.button.textContent).trim() : '';
      this.applyMain = this.dataset.applyMain === 'true';
      this.main = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!this.main || !this.button || typeof IntersectionObserver === 'undefined') {
        this.hidden = true;
        return;
      }
      this.mainText = this.main.querySelector('span') || this.main;
      if (this.escapeTransformedAncestor()) return; // moved to <body>; connectedCallback runs again there

      this.onClick = (event) => {
        event.preventDefault();
        if (this.main.disabled || this.main.getAttribute('aria-disabled') === 'true') return;
        this.main.click();
      };
      this.button.addEventListener('click', this.onClick);

      this.mql = window.matchMedia ? window.matchMedia(MQ) : { matches: true };
      this.onMq = () => this.update();
      if (this.mql.addEventListener) this.mql.addEventListener('change', this.onMq);

      this.past = false;
      this.io = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        // "past" = the main button is out of view and above the viewport (scrolled past it).
        this.past = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.update();
      });
      this.io.observe(this.main);

      this.mo = new MutationObserver(() => this.sync());
      this.mo.observe(this.main, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'], childList: true, subtree: true, characterData: true });
      this.priceSource = document.getElementById(`price-${this.sectionId}`);
      if (this.priceSource && this.priceEl) this.mo.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
      this.sync();
      this.update();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      if (this.mql && this.mql.removeEventListener && this.onMq) this.mql.removeEventListener('change', this.onMq);
      this.setOpen(false);
    }

    /* position: fixed breaks inside a transformed/filtered ancestor (e.g. Dawn's scroll-reveal animation). */
    escapeTransformedAncestor() {
      let el = this.parentElement;
      while (el && el !== document.body) {
        const cs = getComputedStyle(el);
        if ((cs.transform && cs.transform !== 'none') || (cs.filter && cs.filter !== 'none') || /paint|layout|strict|content/.test(cs.contain || '')) {
          const scheme = this.closest('[class*="color-scheme-"]');
          const m = scheme && scheme.className.match(/\bcolor-scheme-[\w-]+\b/);
          if (m) this.classList.add(m[0]);
          document.body.appendChild(this);
          return true;
        }
        el = el.parentElement;
      }
      return false;
    }

    get addToCartText() {
      return (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
    }

    /* Mirror label / disabled / price from the main button and price block. */
    sync() {
      if (!this.main) return;
      // AP – Add-on checklist on the page: it owns the label ("CHOOSE SIGNATURE · $245") on both buttons.
      const checklist = document.querySelector(`ap-addon-checklist[data-section-id="${this.sectionId}"]`);
      const cl = checklist && typeof checklist.labelText === 'function' ? checklist : null;
      if (cl) cl.applyLabel();
      const txt = this.mainText.textContent.trim();
      if (!cl && this.applyMain && !this.main.disabled && txt === this.addToCartText.trim() && this.label && txt !== this.label) {
        this.mainText.textContent = this.label; // Dawn reset it after a variant change
        return; // the observer fires again for this change
      }
      const disabled = this.main.disabled;
      this.button.disabled = disabled;
      const want = disabled ? txt : cl ? cl.labelText() : (this.applyMain ? txt : this.label) || txt;
      if (this.button.textContent !== want) this.button.textContent = want;
      if (this.priceEl && this.priceSource) {
        const src =
          this.priceSource.querySelector('.price__regular .price-item--regular') ||
          this.priceSource.querySelector('.price-item--last') ||
          this.priceSource.querySelector('.price-item');
        const p = src ? src.textContent.trim() : '';
        if (p && this.priceEl.textContent !== p) this.priceEl.textContent = p;
      }
    }

    update() {
      const show = !!(this.past && this.mql.matches);
      if (!show && !this.hidden && this.contains(document.activeElement)) {
        this.hidden = true;
        this.main.focus();
      }
      this.hidden = !show;
      this.setOpen(show);
    }

    setOpen(open) {
      const root = document.documentElement;
      root.classList.toggle('ap-sticky-choose-open', !!open);
      if (open) root.style.setProperty('--ap-sticky-choose-height', `${this.offsetHeight}px`);
      else root.style.removeProperty('--ap-sticky-choose-height');
    }
  }

  customElements.define('ap-sticky-choose', APStickyChoose);
})();
